import { Layout } from '@postscript/components';
import moment from 'moment';
import { MESSAGE_SUBTYPES } from '../constants';
import { StyledMetadata } from '../styles/message';
import { Message, Subscriber } from '../types';
import { MessageFeedback } from './MessageFeedback';

interface MessageMetadataProps {
  message: Message;
  showTimestamp?: boolean;
  subscriber: Subscriber | null;
}

export const isSenderMessage = (message: Message) => {
  // Legacy shopper messages use a uuid as the subtype
  // in order to identify what's up.
  return (
    message.type === 'sender' ||
    (message.type === 'flow_action' &&
      message.subtype &&
      message.subtype.match(
        '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$',
      ))
  );
};

const isShopperMessage = (message: Message) =>
  message.direction === 'outgoing' &&
  message.subtype === MESSAGE_SUBTYPES.SHOPPER;

interface MessageSourceProps {
  message: Message;
  showTimestamp: boolean;
  subscriber: Subscriber | null;
}

const MessageSource = ({
  message,
  showTimestamp,
  subscriber,
}: MessageSourceProps): JSX.Element | null =>
  isShopperMessage(message) ? (
    <Layout align="justify">
      <MessageFeedback
        message={message}
        subscriber={subscriber}
        responseType="shopper"
      />
      <span>
        Sent by Shopper
        {showTimestamp && ` • ${message.createdAt.format('MMM D, h:mm A')}`}
        {message.rcs_message && ' • RCS'}
      </span>
    </Layout>
  ) : isSenderMessage(message) ? (
    <Layout align="right">
      <span style={{ marginRight: 'auto' }}>
        <MessageFeedback
          message={message}
          subscriber={subscriber}
          responseType="sender"
        />
      </span>
      <span>
        {message.scheduled ? (
          <>
            Created by Sender
            {message.scheduledAt && (
              <>
                {' • '}
                Predicted Send:{' '}
                {moment
                  .utc(message.scheduledAt)
                  .local()
                  .format('MMM D, h:mm A')}
              </>
            )}
          </>
        ) : (
          <>
            Sent by Sender
            {showTimestamp && ` • ${message.createdAt.format('MMM D, h:mm A')}`}
          </>
        )}
        {message.rcs_message && ' • RCS'}
      </span>
    </Layout>
  ) : showTimestamp ? (
    <span style={{ marginBottom: 'var(--spacing-2)' }}>
      {message.createdAt.format('MMM D, h:mm A')}
      {message.rcs_message && ' • RCS'}
    </span>
  ) : message.rcs_message ? (
    <span style={{ marginBottom: 'var(--spacing-2)' }}>RCS</span>
  ) : null;

const MessageMetadata = ({
  message,
  showTimestamp = true,
  subscriber,
}: MessageMetadataProps) => {
  return (
    <StyledMetadata
      messageType={message.type}
      incoming={message.direction === 'incoming'}
    >
      <MessageSource
        message={message}
        showTimestamp={showTimestamp}
        subscriber={subscriber}
      />
    </StyledMetadata>
  );
};

export default MessageMetadata;
